(function () {
  'use strict';

  function onClick(instance, event) {
    const adaptiveSuggestionChip = event.target.closest('[data-adaptive-suggestion-chip]');
    if (adaptiveSuggestionChip) {
      const step = adaptiveSuggestionChip.closest('.consultation-step');
      if (step && (step.hidden || step.inert)) return;
      const textarea = step?.querySelector('[data-adaptive-textarea]');
      const submitBtn = step?.querySelector('[data-adaptive-text-submit]');
      const chipContainer = step?.querySelector('[data-adaptive-suggestion-chips]');
      const chipValue = adaptiveSuggestionChip.dataset.suggestionValue || adaptiveSuggestionChip.textContent.trim();

      chipContainer?.querySelectorAll('[data-adaptive-suggestion-chip]').forEach((chip) =>
        chip.classList.remove('anchor-chip--selected'));
      adaptiveSuggestionChip.classList.add('anchor-chip--selected');

      if (textarea) {
        textarea.value = chipValue;
        textarea.dataset.selectedSuggestion = chipValue;
        instance._autoResizeTextarea(textarea);
        textarea.focus();
      }
      if (submitBtn) submitBtn.disabled = false;
      return;
    }

    const onboardingShow = event.target.closest('[data-consultation-onboarding-show]');
    if (onboardingShow) {
      showAnchorVoiceNudge(instance);
      return;
    }

    const onboardingDismiss = event.target.closest('[data-consultation-onboarding-dismiss]');
    if (onboardingDismiss) {
      dismissAnchorVoiceNudge(instance);
      return;
    }

    const onboardingNext = event.target.closest('[data-consultation-onboarding-next]');
    if (onboardingNext) {
      advanceAnchorOnboarding(instance, 1);
      return;
    }

    const onboardingBack = event.target.closest('[data-consultation-onboarding-back]');
    if (onboardingBack) {
      advanceAnchorOnboarding(instance, -1);
      return;
    }

    const chip = event.target.closest('[data-chip]');
    if (chip) {
      const step = chip.closest('.consultation-step');
      if (step && (step.hidden || step.inert)) return;
      selectChip(instance, chip);
      return;
    }

    const option = event.target.closest('.consultation-option');
    if (option) {
      const step = option.closest('.consultation-step');
      if (!step || step.hidden || step.inert || step.getAttribute('aria-hidden') === 'true') {
        instance._log('click-blocked', 'option in inactive step');
        return;
      }
      selectOption(instance, option);
      return;
    }

    const intakeMic = event.target.closest('[data-intake-mic], [data-adaptive-mic]');
    if (intakeMic) {
      const step = intakeMic.closest('.consultation-step');
      if (step && (step.hidden || step.inert)) return;
      if (intakeMic.hasAttribute('data-intake-mic')) dismissAnchorVoiceNudge(instance);
      instance._toggleVoiceRecording(intakeMic);
      return;
    }

    const intakeSubmit = event.target.closest('[data-intake-submit]');
    if (intakeSubmit) {
      const step = intakeSubmit.closest('.consultation-step');
      if (step && (step.hidden || step.inert)) return;
      if (instance._isRecording) {
        instance._stopVoiceRecording();
        return;
      }
      instance._submitAnchorIntake();
      return;
    }

    const adaptiveTextSubmit = event.target.closest('[data-adaptive-text-submit]');
    if (adaptiveTextSubmit) {
      const step = adaptiveTextSubmit.closest('.consultation-step');
      if (step && (step.hidden || step.inert)) return;
      if (instance._isRecording) {
        instance._stopVoiceRecording();
        return;
      }
      instance._submitAdaptiveText();
      return;
    }

    const contactSkip = event.target.closest('[data-contact-skip]');
    if (contactSkip) {
      const step = contactSkip.closest('.consultation-step');
      if (step && (step.hidden || step.inert)) return;
      instance._continueWithoutContact();
      return;
    }

    const backBtn = event.target.closest('[data-consultation-back]');
    if (backBtn) {
      instance._goBack();
    }
  }

  function onKeyDown(instance, event) {
    if (instance.currentStep !== 'anchor' && instance.currentStep !== 'adaptive') return;
    // Without this, Ctrl+A / Cmd+A answers option A and advances. There is no
    // back button, so the consultation is then built on an answer nobody gave.
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return;
    if (event.target.closest && event.target.closest('input, textarea, [contenteditable]')) return;

    const keyMap = { a: 0, b: 1, c: 2, d: 3, e: 4, f: 5 };
    const idx = keyMap[event.key.toLowerCase()];
    if (idx === undefined) return;

    const activeStep = instance.querySelector(`.consultation-step[data-step="${instance.currentStep}"]`);
    if (!activeStep || activeStep.hidden || activeStep.inert) return;
    const options = activeStep.querySelectorAll('.consultation-options .consultation-option');
    if (options && options[idx]) {
      event.preventDefault();
      selectOption(instance, options[idx]);
    }
  }

  function selectOption(instance, option) {
    if (instance._inflight) return;

    const step = option.closest('.consultation-step');
    const stepName = step?.dataset.step;

    if (stepName !== instance.currentStep) {
      instance._log('select-blocked', { stepName, currentStep: instance.currentStep });
      return;
    }

    const allOptions = step.querySelectorAll('.consultation-option');
    allOptions.forEach((entry) => {
      entry.classList.remove('consultation-option--selected');
      entry.setAttribute('aria-checked', 'false');
    });
    option.classList.add('consultation-option--selected');
    option.setAttribute('aria-checked', 'true');

    const value = option.dataset.value;
    const label = option.querySelector('.consultation-option__text')?.textContent?.trim() || value;

    if (instance.currentStep === 'anchor') {
      instance._anchorSelectedValue = value;
      instance._anchorSelectedLabel = label;

      clearTimeout(instance._anchorAutoAdvance);
      const intakeText = instance.querySelector('[data-intake-text]');
      const hasText = intakeText && intakeText.value.trim().length > 0;

      if (hasText) {
        instance._submitAnchorIntake();
      } else {
        instance._anchorAutoAdvance = setTimeout(() => instance._submitAnchorOptionOnly(value, label), 600);
      }
      return;
    }

    if (instance.currentStep === 'adaptive') {
      const questionText = instance.querySelector('[data-adaptive-question]')?.textContent || '';
      const questionId = instance._lastAdaptiveResponse?.question_id || 'unknown';
      const turn = instance._serializeConversationTurn(questionId, questionText, value, label, 'option');

      instance.conversationHistory.push(turn);
      instance.questionCount++;
      instance._setConversationHistory(instance.conversationHistory);

      instance._track('ConsultationProgress', {
        content_name: 'Tara Hair Consultation',
        question_number: instance.questionCount,
        answer: label
      });

      if (instance._lastQuestionComplete) {
        setTimeout(() => instance._enterContactCapture(null), 150);
      } else {
        instance._prefetchNextQuestion();
        setTimeout(() => instance._advanceFromPrefetch(), 150);
      }
    }
  }

  function selectChip(instance, chip) {
    if (instance._inflight) return;

    const allChips = instance.querySelectorAll('[data-chip]');
    const textarea = instance.querySelector('[data-intake-text]');
    const sendBtn = instance.querySelector('[data-intake-submit]');
    const greeting = instance.querySelector('[data-anchor-greeting]');

    if (chip.classList.contains('anchor-chip--selected')) {
      chip.classList.remove('anchor-chip--selected');
      instance._anchorSelectedValue = null;
      instance._anchorSelectedLabel = null;
      if (textarea) textarea.value = '';
      if (sendBtn) sendBtn.disabled = true;
      instance._autoResizeTextarea(textarea);
      return;
    }

    allChips.forEach((entry) => entry.classList.remove('anchor-chip--selected'));
    chip.classList.add('anchor-chip--selected');
    dismissAnchorVoiceNudge(instance);

    const value = chip.dataset.value;
    const label = chip.textContent.trim();
    instance._anchorSelectedValue = value;
    instance._anchorSelectedLabel = label;

    if (textarea) {
      textarea.value = label;
      instance._autoResizeTextarea(textarea);
    }
    if (sendBtn) sendBtn.disabled = false;
    if (greeting) greeting.classList.add('compact');
  }

  function showAnchorVoiceNudge(instance) {
    instance._anchorOnboardingExpanded = true;
    instance._anchorOnboardingDismissed = false;
    instance._anchorOnboardingStep = 0;
    syncAnchorOnboarding(instance);
  }

  function dismissAnchorVoiceNudge(instance) {
    instance._anchorOnboardingExpanded = false;
    instance._setOnboardingSeen();
    syncAnchorOnboarding(instance);
  }

  function resetAnchorVoiceNudge(instance) {
    instance._anchorOnboardingDismissed = false;
    instance._anchorOnboardingExpanded = false;
    instance._anchorOnboardingStep = 0;
    syncAnchorOnboarding(instance);
  }

  function advanceAnchorOnboarding(instance, direction) {
    const currentStep = Number.isFinite(instance._anchorOnboardingStep) ? instance._anchorOnboardingStep : 0;
    instance._anchorOnboardingStep = Math.max(0, Math.min(2, currentStep + direction));
    syncAnchorOnboarding(instance);
  }

  function syncAnchorOnboarding(instance) {
    const banner = instance.querySelector('[data-intake-voice-nudge]');
    const mic = instance.querySelector('[data-intake-mic]');
    const showLink = instance.querySelector('[data-consultation-onboarding-show]');
    // Opt-in: panel only shows when the user explicitly clicks the
    // "How it works" link. No auto-display, even for first-time visitors.
    const shouldShow = instance.currentStep === 'anchor' && instance._anchorOnboardingExpanded === true;
    if (banner) banner.hidden = !shouldShow;
    if (mic) mic.classList.toggle('anchor-prompt__mic--nudge', shouldShow);
    if (showLink) showLink.hidden = instance.currentStep !== 'anchor' || shouldShow;
    const stepIndex = Number.isFinite(instance._anchorOnboardingStep) ? instance._anchorOnboardingStep : 0;

    instance.querySelectorAll('[data-onboarding-step]').forEach((step) => {
      step.hidden = Number(step.dataset.onboardingStep) !== stepIndex || !shouldShow;
    });
    instance.querySelectorAll('[data-onboarding-dot]').forEach((dot) => {
      dot.classList.toggle('is-active', Number(dot.dataset.onboardingDot) === stepIndex && shouldShow);
    });

    const backBtn = instance.querySelector('[data-consultation-onboarding-back]');
    const nextBtn = instance.querySelector('[data-consultation-onboarding-next]');
    const dismissBtn = banner?.querySelector('.consultation-onboarding__cta[data-consultation-onboarding-dismiss]');
    if (backBtn) backBtn.hidden = !shouldShow || stepIndex === 0;
    if (nextBtn) nextBtn.hidden = !shouldShow || stepIndex >= 2;
    if (dismissBtn) dismissBtn.hidden = !shouldShow || stepIndex < 2;
  }

  window.TaraConsultationInteractions = Object.freeze({
    onClick,
    onKeyDown,
    selectOption,
    selectChip,
    showAnchorVoiceNudge,
    dismissAnchorVoiceNudge,
    resetAnchorVoiceNudge,
    advanceAnchorOnboarding,
    syncAnchorOnboarding,
  });
})();
